import { SEO } from '@/components/SEO';

export default function TermsConditions() {
  return (
    <div className="container mx-auto px-4 py-12 max-w-4xl">
      <SEO 
        title="Terms & Conditions - QuickFreeInvoice" 
        description="Review the Terms and Conditions for using QuickFreeInvoice. Read about our free limitations, Pro subscriptions, invoicing terms, and policies." 
        keywords="invoice terms of service, billing terms, quickfreeinvoice terms"
      />
      <h1 className="font-heading text-4xl font-bold mb-8">Terms & Conditions</h1>
      
      <div className="prose prose-lg max-w-none space-y-8">
        <section>
          <h2 className="font-heading text-2xl font-semibold mb-4">1. Acceptance of Terms</h2>
          <p className="text-muted-foreground leading-relaxed">
            By accessing and using quickfreeinvoicetool.com ("the Service"), you accept and agree to be bound 
            by these Terms and Conditions. If you do not agree to these terms, please do not use the Service.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl font-semibold mb-4">2. Service Description</h2>
          <p className="text-muted-foreground leading-relaxed">
            quickfreeinvoicetool.com provides a free and subscription-based invoice and quotation creation tool. The Service 
            allows users to create, customize, save, and export professional invoices, quotes, and estimates in PDF and Word formats.
          </p>
          <p className="text-muted-foreground leading-relaxed mt-3">
            <strong>Free Tier:</strong> Unlimited invoice and quotation creations and PDF downloads, manual business and customer 
            entry each time, ability to save unlimited invoices and quotations, and standard support.
          </p>
          <p className="text-muted-foreground leading-relaxed mt-3">
            <strong>Pro Tier ($5/month):</strong> Unlimited invoice & quotation downloads, unlimited business and customer profiles, 
            unlimited saved invoices and quotations, predefined tax rates setup (auto-fill), direct document sharing links, client view tracking (read receipts),
            direct online payment links (Stripe & PayPal), profile auto-fill, PDF and Word export, and priority email support.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl font-semibold mb-4">3. User Accounts</h2>
          <p className="text-muted-foreground leading-relaxed mb-3">
            To use the Service, you must create an account. You are responsible for:
          </p>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
            <li>Maintaining the confidentiality of your account credentials</li>
            <li>All activities that occur under your account</li>
            <li>Notifying us immediately of any unauthorized use of your account</li>
            <li>Ensuring that your account information is accurate and up-to-date</li>
          </ul>
        </section>

        <section>
          <h2 className="font-heading text-2xl font-semibold mb-4">4. Free Tier Limitations</h2>
          <p className="text-muted-foreground leading-relaxed">
            Free tier accounts are subject to the following limitations:
          </p>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4 mt-3">
            <li>No access to saved Business, Customer, Inventory & Services, and Predefined Tax profiles, and no direct document sharing links (requiring manual details entry each time)</li>
            <li>Manual business entry required for each invoice</li>
            <li>Manual customer entry required for each invoice</li>
            <li>No Word document templates export (limited to PDF downloads)</li>
            <li>Standard support</li>
          </ul>
          <p className="text-muted-foreground leading-relaxed mt-3">
            Upgrade to Pro to unlock automated profile saving, auto-fill, and Word document templates.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl font-semibold mb-4">5. Pro Subscription</h2>
          <p className="text-muted-foreground leading-relaxed mb-3">
            <strong>Billing:</strong> Pro subscriptions are billed monthly at $5 USD per month.
          </p>
          <p className="text-muted-foreground leading-relaxed mb-3">
            <strong>Auto-Renewal:</strong> Your subscription will automatically renew each month unless you cancel 
            before the renewal date.
          </p>
          <p className="text-muted-foreground leading-relaxed mb-3">
            <strong>Cancellation:</strong> You may cancel your subscription at any time through the Subscription page. 
            Upon cancellation, you will retain Pro access until the end of your current billing period. No refunds 
            are provided for partial months.
          </p>
          <p className="text-muted-foreground leading-relaxed">
            <strong>Changes to Pricing:</strong> We reserve the right to change subscription pricing with 30 days' 
            notice. Existing subscribers will be grandfathered at their current price for 6 months after the change.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl font-semibold mb-4">6. Payment Terms</h2>
          <p className="text-muted-foreground leading-relaxed">
            All payments are processed securely through Paddle. By providing payment information, you authorize us 
            to charge the applicable fees to your payment method. Payments are non-refundable except where required 
            by law.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl font-semibold mb-4">7. Prohibited Use</h2>
          <p className="text-muted-foreground leading-relaxed mb-3">
            You agree not to:
          </p>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
            <li>Use the Service for any illegal purposes or to create fraudulent invoices</li>
            <li>Attempt to gain unauthorized access to the Service or other users' accounts</li>
            <li>Upload malicious code, viruses, or harmful content</li>
            <li>Abuse, harass, or harm other users</li>
            <li>Scrape, copy, or reproduce the Service without permission</li>
            <li>Circumvent platform security or other restrictions</li>
          </ul>
        </section>

        <section>
          <h2 className="font-heading text-2xl font-semibold mb-4">8. Intellectual Property</h2>
          <p className="text-muted-foreground leading-relaxed mb-3">
            <strong>Your Content:</strong> You retain all rights to the invoice data, business information, and 
            content you create using the Service. By using the Service, you grant us a limited license to store 
            and display your content solely for the purpose of providing the Service to you.
          </p>
          <p className="text-muted-foreground leading-relaxed">
            <strong>Our Platform:</strong> The Service, including its design, code, features, and trademarks, 
            is owned by quickfreeinvoicetool.com and protected by intellectual property laws.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl font-semibold mb-4">9. Disclaimer</h2>
          <p className="text-muted-foreground leading-relaxed mb-3">
            The Service is provided "as is" without warranties of any kind. We do not guarantee that the Service 
            will be uninterrupted, error-free, or secure. <strong>We are not responsible for the accuracy of tax 
            calculations or invoice content.</strong> You are responsible for ensuring your invoices comply with 
            applicable laws and regulations. Consult a tax professional for advice specific to your situation.
          </p>
          <p className="text-muted-foreground leading-relaxed">
            <strong>No Liability for Fraudulent or Deceptive Documents:</strong> quickfreeinvoicetool.com is a document 
            generation and hosting tool. We do not verify, audit, or authenticate any invoices, quotations, details, or 
            payment links created or sent by users. If a user creates, sends, or shares a fraudulent, deceptive, misleading, 
            or malicious invoice or quotation to any client or third party, we are not responsible, liable, or legally 
            or financially accountable. Clients and recipients are solely responsible for verifying the authenticity 
            of any document and payment request before sending any funds.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl font-semibold mb-4">10. Limitation of Liability</h2>
          <p className="text-muted-foreground leading-relaxed">
            To the fullest extent permitted by law, quickfreeinvoicetool.com shall not be liable for any indirect, 
            incidental, special, consequential, or punitive damages arising from your use of the Service. Our 
            total liability shall not exceed the amount you paid us in the 12 months preceding the claim.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl font-semibold mb-4">11. Account Deletion</h2>
          <p className="text-muted-foreground leading-relaxed">
            You may delete your account at any time from the Profile page. Upon deletion, all your data, including 
            invoices, business profiles, and uploaded files, will be permanently removed from our servers. This 
            action is irreversible.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl font-semibold mb-4">12. Changes to Terms</h2>
          <p className="text-muted-foreground leading-relaxed">
            We may update these Terms and Conditions from time to time. Material changes will be communicated via 
            email. Continued use of the Service after changes constitutes acceptance of the new terms.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl font-semibold mb-4">13. Termination</h2>
          <p className="text-muted-foreground leading-relaxed">
            We reserve the right to suspend or terminate your account if you violate these Terms or engage in 
            fraudulent activity. Upon termination, your right to use the Service will immediately cease.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl font-semibold mb-4">14. Governing Law</h2>
          <p className="text-muted-foreground leading-relaxed">
            These Terms shall be governed by and construed in accordance with the laws of the jurisdiction of 
            registration of the Service Provider, without regard to its conflict of law provisions. Any legal action 
            or proceeding arising under these Terms shall be subject to the exclusive jurisdiction of the competent 
            courts of that jurisdiction.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl font-semibold mb-4">15. Contact Us</h2>
          <p className="text-muted-foreground leading-relaxed">
            If you have any questions about these Terms and Conditions, please contact us at:
          </p>
          <p className="text-muted-foreground leading-relaxed mt-3">
            <strong>Email:</strong>{' '}
            <a href="mailto:support@quickfreeinvoicetool.com" className="text-primary hover:underline">
              support@quickfreeinvoicetool.com
            </a>
          </p>
        </section>

        <div className="border-t pt-8 mt-12">
          <p className="text-sm text-muted-foreground">
            <strong>Last Updated:</strong> {new Date().toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
        </div>
      </div>
    </div>
  );
}
